import { WidgetAutocheck } from "@redotech/redo-model/widget";
import { ShopifyAjaxClient } from "@redotech/shopify-client/ajax";
import { assertNever } from "@redotech/util/type";
import { TryFirstDataSource, TryFirstSourceData } from "./context";
import { getAddToCartButton } from "./pdp-utils";

export async function addStandaloneProductsToCart({
  autocheck,
  productsToAdd,
  sellingPlanId,
}: {
  autocheck: WidgetAutocheck;
  productsToAdd: { productId: string; variantId: string }[];
  sellingPlanId: string;
}) {
  const ajaxClient = ShopifyAjaxClient.fromEnv();

  const items = productsToAdd.map(({ variantId }) => ({
    id: parseInt(variantId, 10),
    quantity: 1,
    selling_plan: parseInt(sellingPlanId, 10),
  }));

  await ajaxClient.cartAdd({ items });
}

export async function addTnplProductToCart(
  params: {
    autocheck: WidgetAutocheck;
    sellingPlanId: string;
  } & TryFirstSourceData,
) {
  const { dataSource, autocheck, sellingPlanId } = params;

  switch (dataSource) {
    case TryFirstDataSource.ATTRIBUTES:
      await addStandaloneProductsToCart({
        autocheck,
        productsToAdd: params.productsToAdd,
        sellingPlanId,
      });
      await updateAndOpenCart();
      break;
    case TryFirstDataSource.FORM:
      if (autocheck.tryFirst?.widget?.clickOriginalAddToCartButton) {
        await addTnplProductViaOriginalButton(
          params.productForm,
          params.sellingPlanId,
        );
      } else {
        // This doesn't reliably trigger refreshes or open cart drawer, but it would be cleaner if we could make it work consistently with different themes.
        await addTnplProductViaApi(params.productForm, sellingPlanId);
        await updateAndOpenCart();
      }
      break;
    default:
      assertNever(dataSource);
      break;
  }
}

export async function addTnplProductViaApi(
  productForm: HTMLElement,
  sellingPlanId: string,
): Promise<void> {
  const formData = new FormData(productForm as unknown as HTMLFormElement);
  const entries = Array.from(formData.entries());

  const variantId = entries.find(([key]) => key === "id")?.[1];
  const quantity = entries.find(([key]) => key === "quantity")?.[1] ?? 1;

  if (!variantId) {
    throw new Error("Product variant ID not found");
  }

  const properties: Record<string, string> = {};
  for (const [key, value] of entries) {
    if (
      key.startsWith("properties[") &&
      key.endsWith("]") &&
      typeof value === "string"
    ) {
      const propKey = key.slice(11, -1);
      properties[propKey] = value;
    }
  }

  const ajaxClient = ShopifyAjaxClient.fromEnv();
  await ajaxClient.cartAdd({
    items: [
      {
        id: parseInt(variantId.toString()),
        quantity: parseInt(quantity.toString(), 10),
        selling_plan: parseInt(sellingPlanId, 10),
        properties,
      },
    ],
  });
}

export async function addTnplProductViaOriginalButton(
  productForm: HTMLElement,
  sellingPlanId: string,
): Promise<void> {
  const submitButton = getAddToCartButton(productForm);

  const doesShopPaySellingPlanDisclosureExistBeforeClick =
    !!productForm.querySelector(
      ".shopify-payment-button #shopify-buyer-consent",
    );

  if (!submitButton) {
    throw new Error("Add to cart button not found");
  }

  const sellingPlanInput = document.createElement("input");
  sellingPlanInput.type = "hidden";
  sellingPlanInput.name = "selling_plan";
  sellingPlanInput.value = sellingPlanId;
  productForm.append(sellingPlanInput);

  // Shop Pay buttons add some disclosure text if there's a selling plan in the product form. Remove it so it doesn't create a weird flash of content.
  if (!doesShopPaySellingPlanDisclosureExistBeforeClick) {
    productForm
      .querySelector(".shopify-payment-button #shopify-buyer-consent")
      ?.remove();
  }

  submitButton.click();

  setTimeout(() => {
    sellingPlanInput.remove();
  }, 100);
}

export const updateAndOpenCart = async () => {
  if (globalThis.Shopify?.shop === "bigshoes-store.myshopify.com") {
    const updatedCart = await (await fetch("/cart.js")).json();
    document.dispatchEvent(
      new CustomEvent("cart:add", { detail: updatedCart }),
    );
    document.dispatchEvent(new CustomEvent("cart:open"));
  }
};
